import {
    Home,
    Building2,
    Hammer,
    Layers,
    Route,
    Trees,
    ClipboardList,
    Wrench,
    ArrowRight,
    CheckCircle2,
} from "lucide-react";

import Hero from "../assests/services/hero.jpg";
import Hero2 from "../assests/services/hero2.jpg";

import Reno1 from "../assests/services/reno.jpg";
import Reno2 from "../assests/services/reno2.jpg";
import Reno3 from "../assests/services/reno3.jpg";

import Drive1 from "../assests/services/drive1.jpg";
import Drive2 from "../assests/services/drive2.jpg";
import Drive3 from "../assests/services/drive3.jpg";

import Cover1 from "../assests/services/cover1.jpg";
import Cover2 from "../assests/services/cover2.jpg";
import Garden from "../assests/services/garden.jpg";
import CTA from "./CTA";

const Services = () => {
    return (
        <div className="pt-20 bg-gradient-to-b from-gray-50 to-white">

            {/* ================= HERO ================= */}
            <section
                className="relative py-32 lg:py-40 bg-cover bg-center overflow-hidden"
                style={{ backgroundImage: `url(${Hero})` }}
            >
                {/* Overlay with gradient */}
                <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/70 to-blue-900/60"></div>

                {/* Animated pattern overlay */}
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0" style={{
                        backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
                        backgroundSize: '40px 40px'
                    }}></div>
                </div>

                <div className="relative max-w-7xl mx-auto px-4 text-center text-white">
                    <div className="inline-block mb-6 px-6 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
                        <span className="text-sm font-semibold tracking-wider text-blue-200">PROFESSIONAL EXCELLENCE</span>
                    </div>

                    <h1 className="text-5xl lg:text-7xl font-bold mb-6 leading-tight">
                        Our Construction
                        <span className="block bg-gradient-to-r from-blue-400 to-cyan-300 bg-clip-text text-transparent mt-2">
                            Services
                        </span>
                    </h1>

                    <p className="text-xl lg:text-2xl text-gray-200 max-w-3xl mx-auto leading-relaxed mb-8">
                        Professional construction solutions delivered with quality,
                        compliance, and attention to detail across the United Kingdom.
                    </p>

                    <div className="flex flex-wrap justify-center gap-4 mt-10">
                        <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-lg border border-white/20">
                            <CheckCircle2 className="text-green-400" size={20} />
                            <span className="text-sm font-medium">UK Certified</span>
                        </div>
                        <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-lg border border-white/20">
                            <CheckCircle2 className="text-green-400" size={20} />
                            <span className="text-sm font-medium">Fully Insured</span>
                        </div>
                        <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-lg border border-white/20">
                            <CheckCircle2 className="text-green-400" size={20} />
                            <span className="text-sm font-medium">Quality Assured</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* ================= SERVICES ================= */}
            <section className="py-24">
                <div className="max-w-7xl mx-auto px-4 space-y-32">

                    {/* Residential */}
                    <ServiceSection
                        icon={Home}
                        title="Residential Construction"
                        text={[
                            "We provide complete residential construction services tailored to homeowners and property developers. From new build homes to extensions and renovations, we deliver practical and well-finished living spaces designed to last.",
                            "Our residential services include new builds, house extensions, loft conversions, structural alterations, and full property renovations. All works are carried out in full compliance with UK Building Regulations and local authority requirements, ensuring safety, comfort, and long-term value.",
                        ]}
                        images={[Hero2]}
                        reverse={false}
                    />

                    {/* Commercial */}
                    <ServiceSection
                        icon={Building2}
                        title="Commercial Construction"
                        text={[
                            "Our commercial construction services support businesses and developers with reliable and efficient building solutions. We deliver offices, retail units, warehouses, and mixed-use developments with a strong focus on functionality and durability.",
                            "We understand the importance of timelines and business continuity, and we carefully plan each project to minimise disruption while ensuring compliance with fire safety, accessibility, and structural standards.",
                        ]}
                        images={[]}
                        reverse={true}
                    />

                    {/* Renovation & Refurbishment */}
                    <ServiceSection
                        icon={Hammer}
                        title="Renovation & Refurbishment"
                        text={[
                            "We specialise in transforming existing properties through professional renovation and refurbishment services. Whether modernising a home or upgrading a commercial property, we deliver high-quality results with minimal disruption.",
                            "Our services include internal remodelling, layout reconfiguration, structural repairs, and full property upgrades, all completed to modern UK standards.",
                        ]}
                        images={[]}
                        reverse={false}
                    />


                    {/* Civil */}
                    <ServiceSection
                        icon={Layers}
                        title="Civil & Structural Works"
                        text={[
                            "Our civil and structural works form the foundation of safe and durable construction projects. We undertake essential groundwork and structural services required for residential, commercial, and infrastructure developments.",
                            "Services include site preparation, excavation, foundations, drainage systems, reinforced concrete works, and external infrastructure, delivered with precision and regulatory compliance.",
                        ]}
                        images={[Reno1, Reno2, Reno3]}
                        reverse={false}
                    />

                    {/* Driveways */}
                    <ServiceSection
                        icon={Route}
                        title="Driveways & External Works"
                        list={[
                            "Block paving driveways",
                            "Tarmac and concrete driveways",
                            "Resin-bound surfaces",
                            "Patios and pathways",
                            "Kerbing and edging",
                            "Surface water drainage solutions",
                        ]}
                        text={[
                            "We design and construct durable and visually appealing driveways and external areas that enhance kerb appeal and property value.",
                            "All external works are designed for longevity, effective drainage, and suitability for UK weather conditions.",
                        ]}
                        images={[Drive1, Drive2, Drive3]}
                        reverse={true}
                    />

                    {/* Extra External Works Images */}
                    <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div className="relative group">
                            <img
                                src={Cover1}
                                alt="External Works"
                                className="w-full h-auto object-cover rounded-2xl shadow-lg group-hover:scale-[1.02] transition-transform duration-300"
                            />
                        </div>

                        <div className="relative group">
                            <img
                                src={Cover2}
                                alt="Driveway Finish"
                                className="w-full  object-cover rounded-2xl shadow-lg group-hover:scale-[1.02] transition-transform duration-300"
                            />
                        </div>
                    </div>


                    {/* Conservatories */}
                    <ServiceSection
                        icon={Trees}
                        title="Conservatories & Garden Rooms"
                        text={[
                            "We design and build bespoke conservatories and garden rooms that create additional living space and a strong connection between indoor and outdoor environments.",
                            "Our services include foundations, structural works, glazing, roofing, insulation, and finishing. Each conservatory or garden room is tailored to complement the existing property while providing year-round comfort and usability.",
                        ]}
                        images={[Garden]}
                        reverse={false}
                    />

                    {/* Project Management */}
                    <ServiceSection
                        icon={ClipboardList}
                        title="Project Management & Site Supervision"
                        text={[
                            "We provide professional project management services to ensure projects are delivered safely, efficiently, and to a high standard.",
                            "Our management services include project planning, contractor coordination, quality inspections, health and safety compliance, and regular progress updates. Clients benefit from having a single point of contact throughout the project.",
                        ]}
                        reverse={true}
                    />

                    {/* Maintenance */}
                    <ServiceSection
                        icon={Wrench}
                        title="Maintenance & Repairs"
                        text={[
                            "We offer planned and reactive maintenance services for residential and commercial properties. Our maintenance solutions help protect building condition and long-term value.",
                            "Services include general building maintenance, structural and cosmetic repairs, emergency works, and ongoing maintenance contracts.",
                        ]}
                        reverse={false}
                    />

                </div>
            </section>
            <CTA />
        </div>
    );
};

/* ================= SERVICE SECTION ================= */

const ServiceSection = ({ icon: Icon, title, text = [], list = [], images = [], reverse = false }) => {
    return (
        <div className={`grid lg:grid-cols-2 gap-16 items-center ${reverse ? 'lg:flex-row-reverse' : ''}`}>

            {/* TEXT */}
            <div className={`${reverse ? 'lg:order-2' : ''}`}>
                <div className="inline-flex items-center gap-4 mb-8 group">
                    <div className="relative">
                        <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-cyan-400 rounded-2xl blur-xl opacity-50 group-hover:opacity-75 transition-opacity"></div>
                        <div className="relative w-16 h-16 rounded-2xl bg-gradient-to-br from-[#0B3C8A] to-blue-700 flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform duration-300">
                            <Icon className="text-white" size={32} />
                        </div>
                    </div>
                    <h2 className="text-4xl font-bold bg-gradient-to-r from-[#082C5A] to-blue-900 bg-clip-text text-transparent">
                        {title}
                    </h2>
                </div>

                <div className="space-y-6">
                    {text.map((p, i) => (
                        <p key={i} className="text-gray-700 text-lg leading-relaxed">
                            {p}
                        </p>
                    ))}

                    {list.length > 0 && (
                        <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-8 border border-blue-100 mt-6">
                            <ul className="space-y-3">
                                {list.map((item, idx) => (
                                    <li key={idx} className="flex items-start gap-3 text-gray-700 font-medium">
                                        <CheckCircle2 className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </div>

            {/* IMAGES */}
            {images.length > 0 && (
                <div className={`${reverse ? 'lg:order-1' : ''}`}>
                    {images.length === 1 ? (
                        <div className="relative group">
                            <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-cyan-400 rounded-3xl blur-2xl opacity-30 group-hover:opacity-50 transition-opacity"></div>
                            <img
                                src={images[0]}
                                alt={title}
                                className="relative rounded-3xl object-cover w-full h-[500px] shadow-2xl group-hover:scale-[1.02] transition-transform duration-500"
                            />
                        </div>
                    ) : images.length === 3 ? (
                        <div className="grid grid-cols-2 gap-6">
                            <div className="relative group col-span-2">
                                <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-cyan-400 rounded-3xl blur-2xl opacity-30 group-hover:opacity-50 transition-opacity"></div>
                                <img
                                    src={images[0]}
                                    alt={title}
                                    className="relative rounded-3xl object-cover h-72 w-full shadow-2xl group-hover:scale-[1.02] transition-transform duration-500"
                                />
                            </div>
                            <div className="relative group">
                                <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-cyan-400 rounded-3xl blur-2xl opacity-30 group-hover:opacity-50 transition-opacity"></div>
                                <img
                                    src={images[1]}
                                    alt={title}
                                    className="relative rounded-3xl object-cover h-64 w-full shadow-2xl group-hover:scale-[1.02] transition-transform duration-500"
                                />
                            </div>
                            <div className="relative group">
                                <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-cyan-400 rounded-3xl blur-2xl opacity-30 group-hover:opacity-50 transition-opacity"></div>
                                <img
                                    src={images[2]}
                                    alt={title}
                                    className="relative rounded-3xl object-cover h-64 w-full shadow-2xl group-hover:scale-[1.02] transition-transform duration-500"
                                />
                            </div>
                        </div>
                    ) : null}
                </div>
            )}
        </div>
    );
};

export default Services;